import React, { useCallback } from "react";
import "./connections.css";
import Toast from "../../uiComponents/Toast";
import Modal from "../../uiComponents/Modal";
import { Info } from "lucide-react";
import { Platform } from "../../../types/social-connect";
import { usePfmSocialConnections } from "../../../hooks/use-pfm-social-connections";
import { BrandDetails } from "../../../types/add-brand-types";

const Connections: React.FC<{
  brandDetails: BrandDetails;
  refreshBrandDetails?: () => void;
}> = ({ brandDetails, refreshBrandDetails }) => {
  const {
    handleConnect,
    handleManage,
    platforms,
    // otherChannels,
    toast,
    handleToastClose,
    socialProfileConnected,
    handleCreateProfile,
    isLoading,
    isManageModalOpen,
    selectedManagePlatform,
    closeManageModal,
    handleDisconnect,
    isDisconnecting,
    handleInsights,
    isInsightsModalOpen,
    selectedInsightsPlatform,
    insightsData,
    isInsightsLoading,
    closeInsightsModal,
  } = usePfmSocialConnections(brandDetails, refreshBrandDetails);

  const isLinkedInPlatform = (platformId: string) =>
    platformId.toLowerCase().replace(/[\s_-]+/g, "") === "linkedin";

  const socialCard = useCallback(
    (platform: Platform) => {
      const isConnected = platform.status === "connected";
      const showLinkedInAnalyticsNotice = isLinkedInPlatform(platform.id);

      return (
        <div
          key={platform.id}
          className="platform-card"
          role="article"
          aria-label={`${platform.name} connection`}
        >
          {isConnected ? (
            <div style={{ marginBottom: 16 }}>
              <div
                className="platform-header-connected"
                style={{ alignItems: "center" }}
              >
                <div
                  className="profile-pic-container"
                  style={{ flexShrink: 0 }}
                >
                  {platform.profilePicture ? (
                    <img
                      src={platform.profilePicture}
                      alt={platform.name}
                      className="profile-pic"
                    />
                  ) : (
                    <div
                      className="profile-pic"
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#e5e7eb",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      {platform.name.charAt(0)}
                    </div>
                  )}
                  <div className="platform-small-icon">
                    <img src={platform.icon} alt="platform" />
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <div
                    className="platform-name-right"
                    style={{ marginBottom: 0 }}
                  >
                    {platform.name}
                  </div>
                  <div
                    className="status-badge status-connected"
                    style={{ flexShrink: 0 }}
                  >
                    <div className="status-dot"></div>
                    Connected
                  </div>
                </div>
              </div>
              {platform.username && (
                <div
                  className="platform-username"
                  style={{
                    marginTop: 12,
                    marginBottom: 0,
                    marginLeft: 2,
                  }}
                  title={platform.username}
                >
                  @
                  {platform.username.length > 50
                    ? `${platform.username.substring(0, 50)}...`
                    : platform.username}
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#9CA3AF",
                      marginTop: 2,
                      fontFamily: "monospace",
                      paddingTop: 5,
                    }}
                  >
                    Last Synced {platform.lastSynced}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="card-top">
                <div className="platform-icon">
                  <img src={platform.icon} alt={platform.name} />
                </div>
              </div>
              <div className="platform-name">{platform.name}</div>
            </>
          )}

          <div className="platform-desc">{platform.description}</div>
          {showLinkedInAnalyticsNotice && (
            <div className="platform-notice platform-notice-info">
              <Info size={14} />
              <span>
                LinkedIn analytics are available only for Company Pages.
                Personal profile metrics are not returned by LinkedIn.
              </span>
            </div>
          )}
          <div className="card-footer">
            {platform.status === "connected" ? (
              <>
                <div className="sync-time"></div>
                <button
                  className="action-btn btn-connect"
                  style={{ marginRight: 8, flex: 1 }}
                  onClick={() => handleInsights(platform)}
                >
                  Insights
                </button>
                <button
                  className="action-btn btn-manage"
                  onClick={() => handleManage(platform)}
                >
                  Manage
                </button>
              </>
            ) : (
              <>
                <div></div>
                <button
                  className="action-btn btn-connect"
                  onClick={async () => await handleConnect(platform.id)}
                >
                  Connect
                </button>
              </>
            )}
          </div>
        </div>
      );
    },
    [handleConnect, handleManage, handleInsights]
  );

  // const showOtherChannels = useCallback(() => {
  //   return (
  //     <div className="other-channels-section">
  //       <h3 className="other-channels-title">Other Channels</h3>
  //       <div className="platforms-grid">
  //         {otherChannels.map((channel) => (
  //           <div key={channel.id} className="other-channel-card">
  //             <div
  //               className="platform-icon"
  //               style={{
  //                 backgroundColor: "#f3f4f6",
  //                 width: "40px",
  //                 height: "40px",
  //               }}
  //             >
  //               <img
  //                 src={channel.icon}
  //                 alt={channel.name}
  //                 style={{ width: "20px", height: "20px" }}
  //               />
  //             </div>
  //             <div className="channel-info">
  //               <div className="channel-name">{channel.name}</div>
  //               <div className="channel-status">
  //                 <div
  //                   className="status-dot"
  //                   style={{ width: "4px", height: "4px" }}
  //                 ></div>
  //                 Active
  //               </div>
  //             </div>
  //             <button
  //               style={{
  //                 background: "none",
  //                 border: "none",
  //                 cursor: "pointer",
  //                 padding: "4px",
  //               }}
  //             >
  //               &#8942;
  //             </button>
  //           </div>
  //         ))}

  //         <div className="request-integration-card">
  //           <div className="add-icon-circle">+</div>
  //           Request Integration
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }, [otherChannels]);

  return (
    <div className="connections-container">
      {isLoading && (
        <div className="connections-loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {!socialProfileConnected && (
        <div className="create-profile-overlay">
          <div className="create-profile-title">Create Social Profile</div>
          <div className="create-profile-desc">
            To connect your social media accounts, you first need to create a
            social profile for your brand. This will help us manage your content
            effectively.
          </div>
          <button className="create-profile-btn" onClick={handleCreateProfile}>
            Create Social Profile
          </button>
        </div>
      )}

      <div className={!socialProfileConnected ? "blurred-overlay" : ""}>
        <div className="connections-header">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <h2 className="connections-title" style={{ marginBottom: 0 }}>
              Connected Platforms
            </h2>
          </div>
          <p className="connections-subtitle">
            Connect your social media accounts to allow the AI agent to publish
            content, analyze performance, and engage with your audience
            automatically.
          </p>
        </div>
        <div className="platforms-grid">
          {platforms.map((platform) => socialCard(platform))}
        </div>
        {/* {showOtherChannels()} */}
      </div>

      <Modal
        isOpen={isManageModalOpen}
        onClose={closeManageModal}
        title={`Manage ${selectedManagePlatform?.name ?? "platform"}`}
        size="small"
      >
        <div style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, color: "#4B5563", fontSize: 14 }}>
            You can disconnect this platform account from your brand.
          </p>
          {selectedManagePlatform?.username && (
            <div style={{ fontSize: 13, color: "#6B7280" }}>
              Connected account:{" "}
              <strong>@{selectedManagePlatform.username}</strong>
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 8,
            }}
          >
            <button
              className="action-btn btn-manage"
              onClick={closeManageModal}
              disabled={isDisconnecting}
            >
              Cancel
            </button>
            <button
              className="action-btn"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              style={{
                background: "#DC2626",
                color: "#fff",
                border: "1px solid #B91C1C",
              }}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isInsightsModalOpen}
        onClose={closeInsightsModal}
        title={`${selectedInsightsPlatform?.name ?? "Platform"} Insights`}
        size="small"
      >
        <div style={{ minHeight: 120 }}>
          {isInsightsLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 40,
              }}
            >
              <div className="spinner"></div>
            </div>
          ) : insightsData ? (
            <>
              {selectedInsightsPlatform && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  {selectedInsightsPlatform.profilePicture ? (
                    <img
                      src={selectedInsightsPlatform.profilePicture}
                      alt={selectedInsightsPlatform.name}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #e5e7eb",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      {selectedInsightsPlatform.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#111827",
                      }}
                    >
                      {selectedInsightsPlatform.name}
                    </div>
                    {selectedInsightsPlatform.username && (
                      <div style={{ fontSize: 13, color: "#6B7280" }}>
                        @{selectedInsightsPlatform.username}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {selectedInsightsPlatform &&
                isLinkedInPlatform(selectedInsightsPlatform.id) && (
                  <div className="platform-notice platform-notice-info insights-notice">
                    <Info size={14} />
                    <span>
                      LinkedIn returns analytics for Company Pages only. If
                      this account is a personal profile, metrics may stay at
                      zero.
                    </span>
                  </div>
                )}
              <div className="insights-stats-grid">
                {[
                  { label: "Total Posts", value: insightsData.total_posts },
                  { label: "Total Views", value: insightsData.total_views },
                  { label: "Total Likes", value: insightsData.total_likes },
                  {
                    label: "Total Comments",
                    value: insightsData.total_comments,
                  },
                  { label: "Total Shares", value: insightsData.total_shares },
                ].map((stat) => (
                  <div key={stat.label} className="insights-stat-card">
                    <div className="insights-stat-value">
                      {stat.value.toLocaleString()}
                    </div>
                    <div className="insights-stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: 24,
                color: "#6B7280",
                fontSize: 14,
              }}
            >
              No insights data available for this account.
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <button
              className="action-btn btn-manage"
              onClick={closeInsightsModal}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {toast && toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Connections;
